import type { UploadAssetDomain } from '@/lib/uploadAssetDomain';
import { abandonUpload, finalizeUpload } from './uploadFinalizers';
import type { UploadBatchState, UploadInfo } from './uploadTypes';

/**
 * Message shown on every file of a batch that failed. A batch is all-or-nothing server-side, so
 * naming only the file that broke would leave the user assuming the rest were saved.
 */
export function batchFailedMessage(
  failedFileName: string | undefined,
  totalFiles: number,
): string {
  const cause = failedFileName
    ? `${failedFileName} failed to upload.`
    : 'The upload failed.';
  return `${cause} None of the ${totalFiles} files were saved. Please re-upload the batch.`;
}

export function createUploadBatchCoordinator(
  updateUploadById: (
    uploadId: string,
    patch: Partial<UploadInfo>,
  ) => void,
  onUploadFinalized: (domain: UploadAssetDomain) => void | Promise<void>,
  abortUploads: (uploadIds: string[]) => void,
) {
  const batches = new Map<string, UploadBatchState>();

  /**
   * Gives up on a whole batch: stops its siblings, releases every reservation the server made for
   * it, and marks each file failed with the same message.
   */
  const failBatch = async (
    batchId: string | undefined,
    failedFileName: string | undefined,
  ) => {
    if (!batchId) return;
    const batch = batches.get(batchId);
    if (!batch || batch.aborting) {
      return;
    }
    batch.aborting = true;

    abortUploads([...batch.uploadIds]);

    const message = batchFailedMessage(
      failedFileName,
      batch.fileIds.length,
    );
    batch.uploadIds.forEach((uploadId) => {
      updateUploadById(uploadId, {
        status: 'failed',
        progress: undefined,
        message,
        batchFailedFileName: failedFileName,
      });
    });

    try {
      await abandonUpload(batch.assetDomain, batch.fileIds);
    } catch (error) {
      // Nothing is saved either way; the reservations just linger until they age out server-side.
      console.error('Failed to release the upload batch', error);
    } finally {
      batches.delete(batchId);
    }
  };

  const tryFinalizeBatch = async (batchId: string) => {
    const batch = batches.get(batchId);
    if (!batch || batch.finalizing || batch.aborting) {
      return;
    }

    const allSucceeded = batch.fileIds.every((fileId) =>
      batch.completedFileIds.has(fileId),
    );
    if (!allSucceeded) {
      return;
    }

    batch.finalizing = true;

    try {
      await finalizeUpload(
        batch.assetDomain,
        batch.fileIds,
        batch.crc32ByFileId,
      );
      batches.delete(batchId);
      await onUploadFinalized(batch.assetDomain);
    } catch (error) {
      // The bytes reached storage, but nothing counts as saved until the batch is finalized — so
      // this releases the reservations like any other batch failure. Not rethrown: failBatch has
      // already put every file in its final state, and letting it propagate would overwrite the
      // batch message on the one file whose promise is unwinding.
      console.error('Failed to finalize the upload batch', error);
      batch.finalizing = false;
      await failBatch(batchId, undefined);
    }
  };

  const finalizeSingleUpload = async (
    assetDomain: UploadAssetDomain,
    fileId: number,
    crc32: string | undefined,
  ) => {
    await finalizeUpload(
      assetDomain,
      [fileId],
      crc32 ? { [fileId]: crc32 } : undefined,
    );
    await onUploadFinalized(assetDomain);
  };

  return {
    registerUpload(
      batchId: string,
      batchFileIds: number[],
      uploadId: string,
      assetDomain: UploadAssetDomain,
    ) {
      const existing = batches.get(batchId);
      if (existing) {
        existing.uploadIds.add(uploadId);
        return;
      }

      batches.set(batchId, {
        fileIds: batchFileIds,
        completedFileIds: new Set(),
        crc32ByFileId: {},
        failedFileIds: new Set(),
        uploadIds: new Set([uploadId]),
        finalizing: false,
        aborting: false,
        assetDomain,
      });
    },

    async markFileFailed(
      batchId: string | undefined,
      fileId: number | undefined,
      failedFileName?: string,
    ) {
      if (!batchId) {
        return;
      }
      const batch = batches.get(batchId);
      if (!batch) {
        return;
      }
      if (fileId !== undefined) {
        batch.failedFileIds.add(fileId);
      }
      await failBatch(batchId, failedFileName);
    },

    async markFileUploaded(
      batchId: string | undefined,
      fileId: number | undefined,
      uploadId: string,
      assetDomain: UploadAssetDomain,
      crc32?: string,
    ) {
      const batch = batchId ? batches.get(batchId) : undefined;
      if (batch?.aborting) {
        return;
      }

      updateUploadById(uploadId, {
        status: 'completed',
        progress: 100,
        message: 'Upload completed successfully',
      });

      if (batchId && fileId !== undefined) {
        if (batch) {
          batch.completedFileIds.add(fileId);
          if (crc32) {
            batch.crc32ByFileId[fileId] = crc32;
          }
          await tryFinalizeBatch(batchId);
        }
        return;
      }

      if (fileId !== undefined) {
        await finalizeSingleUpload(assetDomain, fileId, crc32);
      }
    },

    /** Releases every tracked batch — used when the user dismisses/cancels all uploads. */
    async releaseAll() {
      const tracked = [...batches.entries()];
      batches.clear();
      await Promise.all(
        tracked.map(async ([, batch]) => {
          if (batch.aborting) return;
          try {
            await abandonUpload(batch.assetDomain, batch.fileIds);
          } catch (error) {
            console.error('Failed to release the upload batch', error);
          }
        }),
      );
    },

    clear() {
      batches.clear();
    },
  };
}
